// src/data/ammoDB.js
// DISDownrange Ammunition Database
// All ballistic data sourced from manufacturer published specifications
// Terminal performance ratings based on published gel test research,
// FBI protocol testing, and bullet construction analysis
// Dynamic Innovative Solutions LLC

// ─── Helper functions ─────────────────────────────────────────
export function getAmmoForCaliber(caliber) {
  return AMMO_DB.filter(a => a.calibers.includes(caliber))
}

export function getAmmoForProfile(rifleProfile, useCase = 'all') {
  const { caliber, twist_rate, barrel_length } = rifleProfile

  const MV_CORRECTIONS_556  = { 10:-300, 12:-225, 14.5:-175, 16:-125, 18:-50, 20:0, 22:40, 24:75 }
  const MV_CORRECTIONS_223  = { 16:-100, 18:-50, 20:0, 22:40, 24:75, 26:100 }
  const MV_CORRECTIONS_308  = { 16:-150, 18:-75, 20:0, 22:50, 24:90, 26:120 }
  const MV_CORRECTIONS_65CM = { 18:-100, 20:-40, 22:0, 24:50, 26:90 }
  const MV_CORRECTIONS_7MM  = { 20:-60, 22:0, 24:50, 26:90 }
  const MV_CORRECTIONS_300WM = { 22:-80, 24:0, 26:60, 28:110 }
  const MV_CORRECTIONS_3006 = { 20:-60, 22:0, 24:50, 26:90 }
  const MV_CORRECTIONS_243  = { 20:-50, 22:0, 24:50, 26:80 }
  const MV_CORRECTIONS_270  = { 20:-60, 22:0, 24:50, 26:80 }
  const MV_CORRECTIONS_65PRC = { 20:-80, 22:-30, 24:0, 26:50 }
  const MV_CORRECTIONS_338  = { 24:0, 26:50, 28:90 }
  const MV_CORRECTIONS_350  = { 16:-80, 18:-30, 20:0 }
  const MV_CORRECTIONS_450  = { 16:-60, 18:-20, 20:0 }

  function getMVTable(cal) {
    if (cal.includes('5.56')) return MV_CORRECTIONS_556
    if (cal.includes('.223')) return MV_CORRECTIONS_223
    if (cal.includes('.308') || cal.includes('7.62x51')) return MV_CORRECTIONS_308
    if (cal.includes('6.5 Creedmoor')) return MV_CORRECTIONS_65CM
    if (cal.includes('6.5 PRC')) return MV_CORRECTIONS_65PRC
    if (cal.includes('7mm Rem Mag')) return MV_CORRECTIONS_7MM
    if (cal.includes('.300 Win Mag')) return MV_CORRECTIONS_300WM
    if (cal.includes('.30-06')) return MV_CORRECTIONS_3006
    if (cal.includes('.243')) return MV_CORRECTIONS_243
    if (cal.includes('.270')) return MV_CORRECTIONS_270
    if (cal.includes('.338 Lapua')) return MV_CORRECTIONS_338
    if (cal.includes('.350 Legend')) return MV_CORRECTIONS_350
    if (cal.includes('.450 Bushmaster')) return MV_CORRECTIONS_450
    return {}
  }

  function getMVCorrection(cal, barrelLen) {
    const table = getMVTable(cal)
    if (!table || Object.keys(table).length === 0) return 0
    const lengths = Object.keys(table).map(Number).sort((a, b) => a - b)
    const nearest = lengths.reduce((prev, curr) =>
      Math.abs(curr - barrelLen) < Math.abs(prev - barrelLen) ? curr : prev
    )
    return table[nearest]
  }

  return AMMO_DB.filter(a => {
    if (!a.calibers.includes(caliber)) return false
    if (useCase !== 'all' && !a.use_cases.includes(useCase)) return false
    return true
  }).map(a => ({
    ...a,
    corrected_mv:  a.base_mv + getMVCorrection(caliber, barrel_length),
    twist_warning: twistCheckForAmmo(a, twist_rate, caliber),
  }))
}

function twistCheckForAmmo(ammo, twist_rate, caliber) {
  if (!caliber.includes('5.56') && !caliber.includes('.223')) return null
  const rate = parseFloat(twist_rate.replace('1:', ''))
  const gr   = ammo.grains
  if (rate <= 7  && gr < 62) return `${gr}gr may be overstabilized in ${twist_rate}`
  if (rate >= 9  && gr > 69) return `${gr}gr may be understabilized in ${twist_rate}`
  if (rate >= 12 && gr > 55) return `${gr}gr NOT recommended for ${twist_rate}`
  return null
}

// ─── Ammunition Database ──────────────────────────────────────
export const AMMO_DB = [

  // ══════════════════════════════════════════════════════════
  // 5.56 NATO / .223 REM
  // ══════════════════════════════════════════════════════════

  // ── Range / Training ──────────────────────────────────────
  {
    id: 'xm193_55',
    name: 'Federal XM193 FMJ 55gr',
    calibers: ['5.56 NATO', '.223 Rem'],
    grains: 55, bc_g1: 0.270, bc_g7: null, base_mv: 3165,
    bullet_type: 'FMJ', use_cases: ['range'],
    construction: 'Full Metal Jacket',
    wound_channel: 'FMJ/Tumble', penetration_rating: 2, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: true, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Federal', msrp_per_rd: 0.28,
    notes: 'Standard military ball. 20" barrel baseline. 55gr overstabilized in 1:7 twist.',
  },
  {
    id: 'pmc_bronze_55',
    name: 'PMC Bronze FMJ 55gr',
    calibers: ['5.56 NATO', '.223 Rem'],
    grains: 55, bc_g1: 0.270, bc_g7: null, base_mv: 3100,
    bullet_type: 'FMJ', use_cases: ['range'],
    construction: 'Full Metal Jacket',
    wound_channel: 'FMJ/Tumble', penetration_rating: 2, barrier_rating: 1,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'PMC', msrp_per_rd: 0.24,
    notes: 'Budget brass-cased FMJ. Consistent quality for volume training.',
  },
  {
    id: 'pmc_xtac_otm_77',
    name: 'PMC X-TAC OTM 77gr',
    calibers: ['5.56 NATO'],
    grains: 77, bc_g1: 0.390, bc_g7: 0.196, base_mv: 2750,
    bullet_type: 'OTM', use_cases: ['range'],
    construction: 'Open Tip Match',
    wound_channel: 'OTM/Yaw', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'PMC', msrp_per_rd: 0.55,
    notes: 'Mirrors Black Hills 77gr TMK POI. Good precision practice round for 1:7/1:8 twist.',
  },
  {
    id: 'hornady_73_eldm',
    name: 'Hornady 73gr ELD-M',
    calibers: ['5.56 NATO', '.223 Rem'],
    grains: 73, bc_g1: 0.370, bc_g7: 0.186, base_mv: 2850,
    bullet_type: 'ELD-M', use_cases: ['range'],
    construction: 'ELD Match Heat Shield Tip',
    wound_channel: 'OTM/Yaw', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Hornady', msrp_per_rd: 0.85,
    notes: 'Excellent precision practice round. Heat Shield tip maintains BC at long range.',
  },
  {
    id: 'sierra_69_tmk',
    name: 'Sierra 69gr Tipped MatchKing',
    calibers: ['5.56 NATO', '.223 Rem'],
    grains: 69, bc_g1: 0.372, bc_g7: 0.187, base_mv: 2850,
    bullet_type: 'TMK', use_cases: ['range'],
    construction: 'Tipped Match Boat Tail',
    wound_channel: 'OTM/Yaw', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Sierra', msrp_per_rd: 0.90,
    notes: 'Classic match bullet. Excellent in 1:8 and 1:7 twist barrels.',
  },
  {
    id: 'hornady_75_bthp_match',
    name: 'Hornady 75gr BTHP Match',
    calibers: ['5.56 NATO'],
    grains: 75, bc_g1: 0.395, bc_g7: 0.200, base_mv: 2790,
    bullet_type: 'BTHP', use_cases: ['range'],
    construction: 'Boat Tail Hollow Point Match',
    wound_channel: 'OTM/Yaw', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Hornady', msrp_per_rd: 0.95,
    notes: 'Outstanding accuracy from 1:7-1:8 twist. Popular choice for precision matches.',
  },

  // ── Defense ───────────────────────────────────────────────
  {
    id: 'bh_tmk_77',
    name: 'Black Hills 77gr TMK',
    calibers: ['5.56 NATO'],
    grains: 77, bc_g1: 0.420, bc_g7: 0.211, base_mv: 2750,
    bullet_type: 'OTM', use_cases: ['defense', 'range'],
    construction: 'Tipped MatchKing OTM',
    wound_channel: 'Single Channel', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: 1800, weight_retention_pct: 95,
    fbi_protocol: false, le_adopted: true, ethical_hunting: false, ethical_range_yds: 350,
    manufacturer: 'Black Hills', msrp_per_rd: 1.20,
    best_use: 'Best open-field defensive 5.56 load',
    notes: 'Premium match/defensive. Best BC in 5.56 class. Ideal for 300-500yd defensive use.',
  },
  {
    id: 'speer_gold_dot_75',
    name: 'Speer Gold Dot 75gr HP',
    calibers: ['5.56 NATO'],
    grains: 75, bc_g1: 0.360, bc_g7: 0.181, base_mv: 2975,
    bullet_type: 'HP', use_cases: ['defense'],
    construction: 'Bonded Hollow Point',
    wound_channel: 'Single Channel', penetration_rating: 4, barrier_rating: 5,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 98,
    fbi_protocol: true, le_adopted: true, ethical_hunting: false, ethical_range_yds: 275,
    manufacturer: 'Speer', msrp_per_rd: 1.40,
    notes: 'FBI Protocol tested. Bonded construction. Best CQB barrier performance in 5.56.',
  },
  {
    id: 'hornady_ftx_cd_73',
    name: 'Hornady FTX Critical Defense 73gr',
    calibers: ['5.56 NATO', '.223 Rem'],
    grains: 73, bc_g1: 0.310, bc_g7: null, base_mv: 2780,
    bullet_type: 'FTX', use_cases: ['defense'],
    construction: 'Polymer-Tip Flex Tip HP',
    wound_channel: 'Single Channel', penetration_rating: 3, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1700, weight_retention_pct: 90,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: 225,
    manufacturer: 'Hornady', msrp_per_rd: 1.10,
    notes: 'FTX tip prevents deformation in magazines. Designed for CQB and vehicle scenarios.',
  },
  {
    id: 'federal_tru_64',
    name: 'Federal TRU 64gr SP',
    calibers: ['5.56 NATO', '.223 Rem'],
    grains: 64, bc_g1: 0.330, bc_g7: null, base_mv: 3050,
    bullet_type: 'SP', use_cases: ['defense', 'hunting'],
    construction: 'Soft Point',
    wound_channel: 'Fragmentation', penetration_rating: 4, barrier_rating: 5,
    expansion_reliable: true, min_expansion_vel: 1900, weight_retention_pct: 85,
    fbi_protocol: true, le_adopted: true, ethical_hunting: true, ethical_range_yds: 175,
    manufacturer: 'Federal', msrp_per_rd: 0.90,
    best_use: 'Best dual-purpose AR-15 load',
    notes: 'BEST ALL-AROUND AR-15 LOAD. FBI tested. Dual hunting/defense capability.',
  },
  {
    id: 'armscor_62_psp',
    name: 'Armscor PSP 62gr',
    calibers: ['5.56 NATO', '.223 Rem'],
    grains: 62, bc_g1: 0.310, bc_g7: null, base_mv: 3050,
    bullet_type: 'PSP', use_cases: ['defense', 'range'],
    construction: 'Pointed Soft Point',
    wound_channel: 'Mushroom', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 80,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: 150,
    manufacturer: 'Armscor', msrp_per_rd: 0.42,
    notes: 'Budget soft point. Adequate performance for basic defense/range use.',
  },
  {
    id: 'mk262_mod1_77',
    name: 'Mk 262 Mod 1 OTM 77gr',
    calibers: ['5.56 NATO'],
    grains: 77, bc_g1: 0.420, bc_g7: 0.211, base_mv: 2750,
    bullet_type: 'OTM', use_cases: ['defense'],
    construction: 'Open Tip Match',
    wound_channel: 'Single Channel', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: 1800, weight_retention_pct: 95,
    fbi_protocol: false, le_adopted: true, ethical_hunting: false, ethical_range_yds: 300,
    manufacturer: 'SOCOM/Black Hills', msrp_per_rd: 1.35,
    notes: 'SOCOM adopted precision round. Excellent performance to 600+ yards in 1:7 twist.',
  },

  // ── Hunting ───────────────────────────────────────────────
  {
    id: 'lehigh_cc_62',
    name: 'Lehigh Controlled Chaos 62gr',
    calibers: ['5.56 NATO', '.223 Rem'],
    grains: 62, bc_g1: 0.290, bc_g7: null, base_mv: 3100,
    bullet_type: 'Solid Copper', use_cases: ['hunting'],
    construction: 'Solid Copper Fragmenting',
    wound_channel: 'Multi-Channel + Base', penetration_rating: 4, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 200,
    manufacturer: 'Lehigh Defense', msrp_per_rd: 1.80,
    best_use: 'Best AR-15 hunting load',
    notes: 'Hydraulic fragmentation + base penetration. Highest forgiveness of any AR-15 hunting load.',
  },
  {
    id: 'hornady_gmx_75',
    name: 'Hornady GMX 75gr',
    calibers: ['5.56 NATO', '.223 Rem'],
    grains: 75, bc_g1: 0.383, bc_g7: 0.192, base_mv: 2790,
    bullet_type: 'GMX', use_cases: ['hunting'],
    construction: 'Gilding Metal eXpanding Solid Copper',
    wound_channel: 'Single Controlled', penetration_rating: 5, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 175,
    manufacturer: 'Hornady', msrp_per_rd: 1.55,
    notes: 'Lead-free. Deep controlled penetration. Good for larger deer.',
  },
  {
    id: 'barnes_tsx_70',
    name: 'Barnes TSX 70gr',
    calibers: ['5.56 NATO', '.223 Rem'],
    grains: 70, bc_g1: 0.343, bc_g7: 0.172, base_mv: 2850,
    bullet_type: 'TSX', use_cases: ['hunting'],
    construction: 'Solid Copper HP',
    wound_channel: 'Single Controlled', penetration_rating: 5, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 150,
    manufacturer: 'Barnes', msrp_per_rd: 1.65,
    notes: 'Lead-free solid copper. Deep penetration. Best close-range timber hunting load.',
  },
  {
    id: 'nosler_ballistic_tip_55',
    name: 'Nosler Ballistic Tip 55gr',
    calibers: ['5.56 NATO', '.223 Rem'],
    grains: 55, bc_g1: 0.267, bc_g7: null, base_mv: 3240,
    bullet_type: 'Polymer Tip', use_cases: ['hunting'],
    construction: 'Polymer-Tipped Lead Core',
    wound_channel: 'Fragmentation', penetration_rating: 2, barrier_rating: 1,
    expansion_reliable: true, min_expansion_vel: 2000, weight_retention_pct: 65,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 200,
    manufacturer: 'Nosler', msrp_per_rd: 0.95,
    notes: 'Rapid expansion varmint/predator load. Not ideal for deer-sized game.',
  },

  // ══════════════════════════════════════════════════════════
  // .308 WIN / 7.62x51 NATO
  // ══════════════════════════════════════════════════════════

  // ── Range / Match ─────────────────────────────────────────
  {
    id: 'federal_ae_150_fmj_308',
    name: 'Federal American Eagle 150gr FMJ',
    calibers: ['.308 Win', '7.62x51 NATO'],
    grains: 150, bc_g1: 0.409, bc_g7: 0.206, base_mv: 2820,
    bullet_type: 'FMJ', use_cases: ['range'],
    construction: 'Full Metal Jacket Boat Tail',
    wound_channel: 'FMJ/Tumble', penetration_rating: 3, barrier_rating: 3,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Federal', msrp_per_rd: 0.65,
    notes: 'Standard training round. 24" barrel baseline for .308 Win.',
  },
  {
    id: 'hornady_168_eldm_308',
    name: 'Hornady 168gr ELD-M Match',
    calibers: ['.308 Win'],
    grains: 168, bc_g1: 0.523, bc_g7: 0.263, base_mv: 2700,
    bullet_type: 'ELD-M', use_cases: ['range'],
    construction: 'ELD Match Heat Shield Tip',
    wound_channel: 'OTM/Yaw', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Hornady', msrp_per_rd: 1.45,
    notes: 'Top match load. Stays supersonic past 975 yards. Standard comp bullet.',
  },
  {
    id: 'federal_gm_168_smk_308',
    name: 'Federal Gold Medal 168gr Sierra MatchKing',
    calibers: ['.308 Win'],
    grains: 168, bc_g1: 0.475, bc_g7: 0.239, base_mv: 2650,
    bullet_type: 'BTHP', use_cases: ['range'],
    construction: 'Hollow Point Boat Tail Match',
    wound_channel: 'OTM/Yaw', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: true, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Federal', msrp_per_rd: 1.80,
    notes: 'Gold standard precision .308 load. Preferred by military snipers.',
  },
  {
    id: 'federal_gm_175_smk_308',
    name: 'Federal Gold Medal 175gr Sierra MatchKing',
    calibers: ['.308 Win'],
    grains: 175, bc_g1: 0.505, bc_g7: 0.254, base_mv: 2600,
    bullet_type: 'BTHP', use_cases: ['range'],
    construction: 'Hollow Point Boat Tail Match',
    wound_channel: 'OTM/Yaw', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: true, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Federal', msrp_per_rd: 1.90,
    notes: 'M118LR military equivalent. Superior long-range performance over 168gr.',
  },
  {
    id: 'hornady_155_amax_308',
    name: 'Hornady Black 155gr A-MAX',
    calibers: ['.308 Win'],
    grains: 155, bc_g1: 0.435, bc_g7: 0.219, base_mv: 2850,
    bullet_type: 'A-MAX', use_cases: ['range'],
    construction: 'Polymer-Tipped Match',
    wound_channel: 'OTM/Yaw', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Hornady', msrp_per_rd: 1.10,
    notes: 'Flat trajectory at 2,850 fps. Designed for AR-10 platform.',
  },

  // ── Hunting ───────────────────────────────────────────────
  {
    id: 'hornady_150_interlock_308',
    name: 'Hornady American Whitetail 150gr SP InterLock',
    calibers: ['.308 Win'],
    grains: 150, bc_g1: 0.338, bc_g7: null, base_mv: 2820,
    bullet_type: 'SP', use_cases: ['hunting'],
    construction: 'Soft Point InterLock',
    wound_channel: 'Controlled Expansion', penetration_rating: 4, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 80,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 350,
    manufacturer: 'Hornady', msrp_per_rd: 0.95,
    notes: 'Classic deer hunting load. InterLock ring retains core/jacket during expansion.',
  },
  {
    id: 'hornady_178_eldx_308',
    name: 'Hornady Precision Hunter 178gr ELD-X',
    calibers: ['.308 Win'],
    grains: 178, bc_g1: 0.552, bc_g7: 0.278, base_mv: 2600,
    bullet_type: 'ELD-X', use_cases: ['hunting'],
    construction: 'Polymer-Tipped Bonded Expanding',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 88,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 500,
    manufacturer: 'Hornady', msrp_per_rd: 2.10,
    best_use: 'Best long-range .308 hunting load',
    notes: 'Expands reliably 1,600-3,000 fps. 85-90% weight retention. Best all-around .308 hunt load.',
  },
  {
    id: 'federal_165_tsx_308',
    name: 'Federal Premium 165gr Barnes TSX',
    calibers: ['.308 Win'],
    grains: 165, bc_g1: 0.445, bc_g7: 0.224, base_mv: 2650,
    bullet_type: 'TSX', use_cases: ['hunting'],
    construction: 'Solid Copper HP Lead-Free',
    wound_channel: 'Single Controlled', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 400,
    manufacturer: 'Federal', msrp_per_rd: 2.50,
    notes: 'Lead-free. 100% weight retention. Excellent for states with lead-free requirements.',
  },
  {
    id: 'federal_165_partition_308',
    name: 'Federal Premium 165gr Nosler Partition',
    calibers: ['.308 Win'],
    grains: 165, bc_g1: 0.435, bc_g7: null, base_mv: 2700,
    bullet_type: 'Partition', use_cases: ['hunting'],
    construction: 'Dual-Core Partition',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1500, weight_retention_pct: 95,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 400,
    manufacturer: 'Federal', msrp_per_rd: 2.80,
    notes: 'Classic partition design. Front expands, rear drives. Proven on elk and moose.',
  },
  {
    id: 'federal_150_powershok_308',
    name: 'Federal Power-Shok 150gr JSP',
    calibers: ['.308 Win'],
    grains: 150, bc_g1: 0.343, bc_g7: null, base_mv: 2820,
    bullet_type: 'JSP', use_cases: ['hunting'],
    construction: 'Jacketed Soft Point',
    wound_channel: 'Mushroom Expansion', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 75,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 300,
    manufacturer: 'Federal', msrp_per_rd: 0.85,
    notes: 'Budget hunting load. Reliable expansion for whitetail inside 300 yards.',
  },
  {
    id: 'winchester_150_pp_308',
    name: 'Winchester Power Point 150gr',
    calibers: ['.308 Win'],
    grains: 150, bc_g1: 0.314, bc_g7: null, base_mv: 2820,
    bullet_type: 'SP', use_cases: ['hunting'],
    construction: 'Notched Jacket Soft Point',
    wound_channel: 'Mushroom Expansion', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 70,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 300,
    manufacturer: 'Winchester', msrp_per_rd: 0.80,
    notes: 'Budget hunting staple. Notched jacket ensures reliable expansion.',
  },

  // ── Defense ───────────────────────────────────────────────
  {
    id: 'federal_fusion_150_308',
    name: 'Federal Fusion 150gr FSP',
    calibers: ['.308 Win'],
    grains: 150, bc_g1: 0.409, bc_g7: null, base_mv: 2820,
    bullet_type: 'FSP', use_cases: ['hunting', 'defense'],
    construction: 'Fusion Bonded Soft Point',
    wound_channel: 'Controlled Expansion', penetration_rating: 4, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1700, weight_retention_pct: 90,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 400,
    manufacturer: 'Federal', msrp_per_rd: 1.10,
    notes: 'Electrochemically bonded core/jacket. Good dual-purpose hunting/defensive load.',
  },

  // ══════════════════════════════════════════════════════════
  // 6.5 CREEDMOOR
  // ══════════════════════════════════════════════════════════

  {
    id: 'hornady_140_eldm_65cm',
    name: 'Hornady 140gr ELD-M Match',
    calibers: ['6.5 Creedmoor'],
    grains: 140, bc_g1: 0.610, bc_g7: 0.307, base_mv: 2710,
    bullet_type: 'ELD-M', use_cases: ['range'],
    construction: 'ELD Match Heat Shield Tip',
    wound_channel: 'OTM/Yaw', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Hornady', msrp_per_rd: 1.50,
    notes: 'Premier 6.5 CM match load. Stays supersonic past 1,200 yards. G7 BC verified by Doppler.',
  },
  {
    id: 'hornady_143_eldx_65cm',
    name: 'Hornady Precision Hunter 143gr ELD-X',
    calibers: ['6.5 Creedmoor'],
    grains: 143, bc_g1: 0.625, bc_g7: 0.315, base_mv: 2700,
    bullet_type: 'ELD-X', use_cases: ['hunting'],
    construction: 'Polymer-Tipped Bonded Expanding',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 88,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 700,
    manufacturer: 'Hornady', msrp_per_rd: 2.20,
    best_use: 'Best 6.5 Creedmoor hunting load',
    notes: '1,020 ft-lbs at 700 yards. Expands at 1,600 fps minimum. Exceptional long-range hunting.',
  },
  {
    id: 'federal_130_ta_65cm',
    name: 'Federal Premium 130gr Terminal Ascent',
    calibers: ['6.5 Creedmoor'],
    grains: 130, bc_g1: 0.532, bc_g7: 0.268, base_mv: 2825,
    bullet_type: 'Terminal Ascent', use_cases: ['hunting'],
    construction: 'Bonded Polymer Tip Expanding',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1500, weight_retention_pct: 95,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 600,
    manufacturer: 'Federal', msrp_per_rd: 2.50,
    notes: 'Expands at any distance. Flexible nose pocket initiates expansion from 1,500 fps.',
  },
  {
    id: 'nosler_140_accubond_65cm',
    name: 'Nosler 140gr AccuBond',
    calibers: ['6.5 Creedmoor'],
    grains: 140, bc_g1: 0.509, bc_g7: 0.256, base_mv: 2750,
    bullet_type: 'AccuBond', use_cases: ['hunting'],
    construction: 'Bonded Polymer Tip',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 90,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 550,
    manufacturer: 'Nosler', msrp_per_rd: 2.20,
    notes: 'Classic bonded bullet. Proven on elk and deer to 500+ yards.',
  },
  {
    id: 'hornady_120_sst_65cm',
    name: 'Hornady Superformance 120gr SST',
    calibers: ['6.5 Creedmoor'],
    grains: 120, bc_g1: 0.458, bc_g7: 0.230, base_mv: 3050,
    bullet_type: 'SST', use_cases: ['hunting'],
    construction: 'Polymer-Tipped InterLock',
    wound_channel: 'Rapid Expansion', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 70,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 400,
    manufacturer: 'Hornady', msrp_per_rd: 1.75,
    notes: 'Highest velocity 6.5 CM factory load at 3,050 fps. Good for close-range deer.',
  },
  {
    id: 'winchester_140_pp_65cm',
    name: 'Winchester 140gr Power Point',
    calibers: ['6.5 Creedmoor'],
    grains: 140, bc_g1: 0.485, bc_g7: null, base_mv: 2750,
    bullet_type: 'SP', use_cases: ['hunting'],
    construction: 'Notched Jacket Soft Point',
    wound_channel: 'Mushroom Expansion', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 75,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 350,
    manufacturer: 'Winchester', msrp_per_rd: 1.20,
    notes: 'Budget hunting load for 6.5 CM. Reliable expansion on deer-sized game.',
  },
  {
    id: 'berger_140_eol_65cm',
    name: 'Berger 140gr EOL Elite Hunter',
    calibers: ['6.5 Creedmoor'],
    grains: 140, bc_g1: 0.617, bc_g7: 0.310, base_mv: 2710,
    bullet_type: 'EOL', use_cases: ['hunting'],
    construction: 'Expanding Open Tip Lead',
    wound_channel: 'Expansion + Fragmentation', penetration_rating: 4, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1500, weight_retention_pct: 80,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 600,
    manufacturer: 'Berger', msrp_per_rd: 2.80,
    notes: 'Extreme long range. Near-match BC with reliable expansion at 1,500 fps minimum.',
  },

  // ══════════════════════════════════════════════════════════
  // 7mm REMINGTON MAGNUM
  // ══════════════════════════════════════════════════════════

  {
    id: 'hornady_162_eldx_7mm',
    name: 'Hornady Precision Hunter 162gr ELD-X',
    calibers: ['7mm Rem Mag'],
    grains: 162, bc_g1: 0.625, bc_g7: 0.313, base_mv: 2940,
    bullet_type: 'ELD-X', use_cases: ['hunting'],
    construction: 'Polymer-Tipped Bonded Expanding',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 88,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 700,
    manufacturer: 'Hornady', msrp_per_rd: 2.20,
    best_use: 'Primary load — Remington 770',
    notes: 'RECOMMENDED PRIMARY LOAD. 315yd MPBR on 200yd zero. Use 25/300 zero for 375yd MPBR.',
  },
  {
    id: 'berger_168_vld_7mm',
    name: 'Berger VLD Hunting 168gr',
    calibers: ['7mm Rem Mag'],
    grains: 168, bc_g1: 0.617, bc_g7: 0.310, base_mv: 2900,
    bullet_type: 'VLD', use_cases: ['hunting'],
    construction: 'Polymer-Tip Very Low Drag',
    wound_channel: 'Expansion + Fragmentation', penetration_rating: 4, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1500, weight_retention_pct: 80,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 600,
    manufacturer: 'Berger', msrp_per_rd: 2.50,
    notes: 'Violent initial expansion creates wide wound channel. Near-match BC.',
  },
  {
    id: 'federal_160_partition_7mm',
    name: 'Federal Nosler Partition 160gr',
    calibers: ['7mm Rem Mag'],
    grains: 160, bc_g1: 0.519, bc_g7: 0.261, base_mv: 2950,
    bullet_type: 'Partition', use_cases: ['hunting'],
    construction: 'Dual-Core Partition',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1500, weight_retention_pct: 95,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 500,
    manufacturer: 'Federal', msrp_per_rd: 2.80,
    notes: 'Classic partition. Excellent on elk, moose, and large deer.',
  },
  {
    id: 'hornady_154_sst_7mm',
    name: 'Hornady Superformance 154gr SST',
    calibers: ['7mm Rem Mag'],
    grains: 154, bc_g1: 0.530, bc_g7: 0.267, base_mv: 3035,
    bullet_type: 'SST', use_cases: ['hunting'],
    construction: 'Polymer-Tipped InterLock',
    wound_channel: 'Rapid Expansion', penetration_rating: 4, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 75,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 450,
    manufacturer: 'Hornady', msrp_per_rd: 1.90,
    notes: 'Highest MV in 7mm lineup at 3,035fps. Flat shooting close-range deer load.',
  },
  {
    id: 'sb_173_sp_7mm',
    name: 'S&B Soft Point 173gr',
    calibers: ['7mm Rem Mag'],
    grains: 173, bc_g1: 0.493, bc_g7: 0.248, base_mv: 2920,
    bullet_type: 'SP', use_cases: ['hunting'],
    construction: 'Soft Point',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1500, weight_retention_pct: 90,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 450,
    manufacturer: 'Sellier & Bellot', msrp_per_rd: 1.40,
    notes: 'Heaviest 7mm Rem Mag at 173gr. Best value hunting load. Deep penetration.',
  },
  {
    id: 'winchester_150_pp_7mm',
    name: 'Winchester Power Point 150gr',
    calibers: ['7mm Rem Mag'],
    grains: 150, bc_g1: 0.433, bc_g7: null, base_mv: 3090,
    bullet_type: 'SP', use_cases: ['hunting'],
    construction: 'Notched Jacket Soft Point',
    wound_channel: 'Mushroom Expansion', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 72,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 350,
    manufacturer: 'Winchester', msrp_per_rd: 1.20,
    notes: 'Budget 7mm Rem Mag. High velocity at 3,090fps. Good for close-range deer.',
  },

  // ══════════════════════════════════════════════════════════
  // .300 WIN MAG
  // ══════════════════════════════════════════════════════════

  {
    id: 'hornady_178_eldx_300wm',
    name: 'Hornady Precision Hunter 178gr ELD-X',
    calibers: ['.300 Win Mag'],
    grains: 178, bc_g1: 0.552, bc_g7: 0.278, base_mv: 2960,
    bullet_type: 'ELD-X', use_cases: ['hunting'],
    construction: 'Polymer-Tipped Bonded Expanding',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 88,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 800,
    manufacturer: 'Hornady', msrp_per_rd: 2.40,
    best_use: 'Best all-around .300 Win Mag hunting load',
    notes: 'High BC + high velocity = outstanding long-range performance. Elk and moose capable.',
  },
  {
    id: 'federal_180_trophy_bonded_300wm',
    name: 'Federal Premium 180gr Trophy Bonded Tip',
    calibers: ['.300 Win Mag'],
    grains: 180, bc_g1: 0.520, bc_g7: 0.262, base_mv: 2960,
    bullet_type: 'Trophy Bonded Tip', use_cases: ['hunting'],
    construction: 'Bonded Polymer Tip',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1500, weight_retention_pct: 95,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 700,
    manufacturer: 'Federal', msrp_per_rd: 3.00,
    notes: 'Proven bonded bullet. Excellent on all North American big game including bear.',
  },
  {
    id: 'winchester_180_pp_300wm',
    name: 'Winchester Power Point 180gr',
    calibers: ['.300 Win Mag'],
    grains: 180, bc_g1: 0.431, bc_g7: null, base_mv: 2960,
    bullet_type: 'SP', use_cases: ['hunting'],
    construction: 'Notched Jacket Soft Point',
    wound_channel: 'Mushroom Expansion', penetration_rating: 4, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 75,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 500,
    manufacturer: 'Winchester', msrp_per_rd: 1.80,
    notes: 'Classic budget .300 WM. Reliable expansion on elk and deer inside 500 yards.',
  },
  {
    id: 'hornady_200_eldx_300wm',
    name: 'Hornady Precision Hunter 200gr ELD-X',
    calibers: ['.300 Win Mag'],
    grains: 200, bc_g1: 0.597, bc_g7: 0.300, base_mv: 2850,
    bullet_type: 'ELD-X', use_cases: ['hunting'],
    construction: 'Polymer-Tipped Bonded Expanding',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 5,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 90,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 800,
    manufacturer: 'Hornady', msrp_per_rd: 2.60,
    notes: 'Heaviest .300 WM hunting load. Best for large dangerous game. Outstanding BC.',
  },
  {
    id: 'federal_215_berger_300wm',
    name: 'Federal Premium 215gr Berger Hybrid Hunter',
    calibers: ['.300 Win Mag'],
    grains: 215, bc_g1: 0.640, bc_g7: 0.331, base_mv: 2810,
    bullet_type: 'Hybrid Hunter', use_cases: ['hunting'],
    construction: 'Polymer-Tipped OTM Hybrid',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 85,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 1000,
    manufacturer: 'Federal', msrp_per_rd: 4.00,
    notes: 'Highest BC .300 WM hunting load at .640 G1. Purpose-built for ELR hunting.',
  },
  {
    id: 'hornady_168_eldm_300wm',
    name: 'Hornady Match 168gr ELD-M',
    calibers: ['.300 Win Mag'],
    grains: 168, bc_g1: 0.523, bc_g7: 0.263, base_mv: 3050,
    bullet_type: 'ELD-M', use_cases: ['range'],
    construction: 'ELD Match Heat Shield',
    wound_channel: 'OTM/Yaw', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Hornady', msrp_per_rd: 2.20,
    notes: 'Match load. Faster than .308 equivalent at 3,050fps. Excellent for F-Class.',
  },

  // ══════════════════════════════════════════════════════════
  // .30-06 SPRINGFIELD
  // ══════════════════════════════════════════════════════════

  {
    id: 'federal_150_fusion_3006',
    name: 'Federal Fusion 150gr',
    calibers: ['.30-06 Springfield'],
    grains: 150, bc_g1: 0.409, bc_g7: null, base_mv: 2910,
    bullet_type: 'Fusion', use_cases: ['hunting'],
    construction: 'Fusion Bonded Soft Point',
    wound_channel: 'Controlled Expansion', penetration_rating: 4, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1700, weight_retention_pct: 90,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 450,
    manufacturer: 'Federal', msrp_per_rd: 1.20,
    notes: 'Classic 30-06 whitetail load. Bonded core delivers reliable expansion.',
  },
  {
    id: 'hornady_165_sst_3006',
    name: 'Hornady American Whitetail 165gr SP',
    calibers: ['.30-06 Springfield'],
    grains: 165, bc_g1: 0.447, bc_g7: null, base_mv: 2800,
    bullet_type: 'SP', use_cases: ['hunting'],
    construction: 'InterLock Soft Point',
    wound_channel: 'Controlled Expansion', penetration_rating: 4, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 80,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 400,
    manufacturer: 'Hornady', msrp_per_rd: 1.15,
    notes: 'Standard whitetail load for 30-06. Excellent performance inside 400 yards.',
  },
  {
    id: 'remington_180_corelokt_3006',
    name: 'Remington Core-Lokt 180gr PSP',
    calibers: ['.30-06 Springfield'],
    grains: 180, bc_g1: 0.383, bc_g7: null, base_mv: 2700,
    bullet_type: 'PSP', use_cases: ['hunting'],
    construction: 'Core-Lokt Pointed Soft Point',
    wound_channel: 'Controlled Expansion', penetration_rating: 4, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 75,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 350,
    manufacturer: 'Remington', msrp_per_rd: 0.95,
    notes: 'Most popular 30-06 hunting load in history. Reliable on deer and black bear.',
  },
  {
    id: 'federal_165_trophy_3006',
    name: 'Federal Premium 165gr Trophy Bonded Tip',
    calibers: ['.30-06 Springfield'],
    grains: 165, bc_g1: 0.490, bc_g7: null, base_mv: 2800,
    bullet_type: 'Trophy Bonded Tip', use_cases: ['hunting'],
    construction: 'Bonded Polymer Tip',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1500, weight_retention_pct: 95,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 550,
    manufacturer: 'Federal', msrp_per_rd: 2.40,
    notes: 'Premium bonded load for 30-06. Handles elk and moose at extended ranges.',
  },

  // ══════════════════════════════════════════════════════════
  // .243 WIN
  // ══════════════════════════════════════════════════════════

  {
    id: 'federal_100_fusion_243',
    name: 'Federal Fusion 100gr',
    calibers: ['.243 Win'],
    grains: 100, bc_g1: 0.356, bc_g7: null, base_mv: 2960,
    bullet_type: 'Fusion', use_cases: ['hunting'],
    construction: 'Fusion Bonded Soft Point',
    wound_channel: 'Controlled Expansion', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1700, weight_retention_pct: 88,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 400,
    manufacturer: 'Federal', msrp_per_rd: 1.40,
    notes: 'Standard .243 deer load. Light recoil makes it ideal for youth hunters.',
  },
  {
    id: 'hornady_95_sst_243',
    name: 'Hornady Superformance 95gr SST',
    calibers: ['.243 Win'],
    grains: 95, bc_g1: 0.355, bc_g7: null, base_mv: 3120,
    bullet_type: 'SST', use_cases: ['hunting'],
    construction: 'Polymer-Tipped InterLock',
    wound_channel: 'Rapid Expansion', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 70,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 400,
    manufacturer: 'Hornady', msrp_per_rd: 1.50,
    notes: 'High velocity .243 at 3,120fps. Flat shooting deer and antelope load.',
  },
  {
    id: 'winchester_80_varmint_243',
    name: 'Winchester Super-X 80gr Soft Point',
    calibers: ['.243 Win'],
    grains: 80, bc_g1: 0.294, bc_g7: null, base_mv: 3350,
    bullet_type: 'SP', use_cases: ['hunting', 'range'],
    construction: 'Soft Point',
    wound_channel: 'Fragmentation', penetration_rating: 2, barrier_rating: 1,
    expansion_reliable: true, min_expansion_vel: 2000, weight_retention_pct: 60,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 250,
    manufacturer: 'Winchester', msrp_per_rd: 0.85,
    notes: 'Varmint and predator load. High velocity fragmenting performance.',
  },

  // ══════════════════════════════════════════════════════════
  // .270 WIN
  // ══════════════════════════════════════════════════════════

  {
    id: 'hornady_140_eldx_270',
    name: 'Hornady Precision Hunter 140gr ELD-X',
    calibers: ['.270 Win'],
    grains: 140, bc_g1: 0.536, bc_g7: 0.270, base_mv: 2950,
    bullet_type: 'ELD-X', use_cases: ['hunting'],
    construction: 'Polymer-Tipped Bonded Expanding',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 88,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 600,
    manufacturer: 'Hornady', msrp_per_rd: 2.10,
    best_use: 'Best long-range .270 hunting load',
    notes: 'Excellent BC for .270. Reliable expansion to extreme ranges. Great deer/elk load.',
  },
  {
    id: 'federal_130_fusion_270',
    name: 'Federal Fusion 130gr',
    calibers: ['.270 Win'],
    grains: 130, bc_g1: 0.414, bc_g7: null, base_mv: 3060,
    bullet_type: 'Fusion', use_cases: ['hunting'],
    construction: 'Fusion Bonded Soft Point',
    wound_channel: 'Controlled Expansion', penetration_rating: 4, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1700, weight_retention_pct: 90,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 450,
    manufacturer: 'Federal', msrp_per_rd: 1.30,
    notes: 'High velocity 130gr at 3,060fps. Standard deer hunting .270 load.',
  },
  {
    id: 'remington_130_corelokt_270',
    name: 'Remington Core-Lokt 130gr PSP',
    calibers: ['.270 Win'],
    grains: 130, bc_g1: 0.336, bc_g7: null, base_mv: 3060,
    bullet_type: 'PSP', use_cases: ['hunting'],
    construction: 'Core-Lokt Pointed Soft Point',
    wound_channel: 'Mushroom Expansion', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1800, weight_retention_pct: 72,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 350,
    manufacturer: 'Remington', msrp_per_rd: 0.90,
    notes: 'Classic .270 hunting load. Proven whitetail performance for decades.',
  },

  // ══════════════════════════════════════════════════════════
  // 6.5 PRC
  // ══════════════════════════════════════════════════════════

  {
    id: 'hornady_143_eldx_65prc',
    name: 'Hornady Precision Hunter 143gr ELD-X',
    calibers: ['6.5 PRC'],
    grains: 143, bc_g1: 0.625, bc_g7: 0.315, base_mv: 2960,
    bullet_type: 'ELD-X', use_cases: ['hunting'],
    construction: 'Polymer-Tipped Bonded Expanding',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 88,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 800,
    manufacturer: 'Hornady', msrp_per_rd: 2.40,
    best_use: 'Best 6.5 PRC hunting load',
    notes: '2,960fps with same ELD-X as 6.5 CM but 260fps faster. Outstanding ELR performance.',
  },
  {
    id: 'federal_130_ta_65prc',
    name: 'Federal Premium 130gr Terminal Ascent',
    calibers: ['6.5 PRC'],
    grains: 130, bc_g1: 0.532, bc_g7: 0.268, base_mv: 3050,
    bullet_type: 'Terminal Ascent', use_cases: ['hunting'],
    construction: 'Bonded Polymer Tip Expanding',
    wound_channel: 'Controlled Expansion', penetration_rating: 5, barrier_rating: 4,
    expansion_reliable: true, min_expansion_vel: 1500, weight_retention_pct: 95,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 700,
    manufacturer: 'Federal', msrp_per_rd: 2.80,
    notes: 'Fastest 6.5 PRC factory load at 3,050fps. Expansion guaranteed at any range.',
  },
  {
    id: 'hornady_147_eldm_65prc',
    name: 'Hornady Match 147gr ELD-M',
    calibers: ['6.5 PRC'],
    grains: 147, bc_g1: 0.697, bc_g7: 0.351, base_mv: 2910,
    bullet_type: 'ELD-M', use_cases: ['range'],
    construction: 'ELD Match Heat Shield',
    wound_channel: 'OTM/Yaw', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: false, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Hornady', msrp_per_rd: 2.20,
    notes: 'Highest BC 6.5 PRC match load at .697 G1. Stays supersonic past 1,400 yards.',
  },

  // ══════════════════════════════════════════════════════════
  // .338 LAPUA MAGNUM
  // ══════════════════════════════════════════════════════════

  {
    id: 'hornady_285_eldm_338',
    name: 'Hornady Match 285gr ELD-M',
    calibers: ['.338 Lapua Mag'],
    grains: 285, bc_g1: 0.789, bc_g7: 0.397, base_mv: 2745,
    bullet_type: 'ELD-M', use_cases: ['range'],
    construction: 'ELD Match Heat Shield',
    wound_channel: 'OTM/Yaw', penetration_rating: 4, barrier_rating: 4,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: true, ethical_hunting: false, ethical_range_yds: null,
    manufacturer: 'Hornady', msrp_per_rd: 4.50,
    notes: 'Premier .338 Lapua match load. Stays supersonic past 1,600 yards.',
  },
  {
    id: 'federal_250_berger_338',
    name: 'Federal Premium 250gr Berger Juggernaut OTM',
    calibers: ['.338 Lapua Mag'],
    grains: 250, bc_g1: 0.700, bc_g7: 0.352, base_mv: 2950,
    bullet_type: 'OTM', use_cases: ['range', 'hunting'],
    construction: 'Open Tip Match Boat Tail',
    wound_channel: 'OTM/Yaw', penetration_rating: 4, barrier_rating: 4,
    expansion_reliable: false, min_expansion_vel: null, weight_retention_pct: 100,
    fbi_protocol: false, le_adopted: true, ethical_hunting: true, ethical_range_yds: 1000,
    manufacturer: 'Federal', msrp_per_rd: 5.00,
    notes: 'Military/LE sniper load. Effective on large game to 1,000+ yards.',
  },

  // ══════════════════════════════════════════════════════════
  // .350 LEGEND
  // ══════════════════════════════════════════════════════════

  {
    id: 'winchester_180_pp_350',
    name: 'Winchester Power Point 180gr',
    calibers: ['.350 Legend'],
    grains: 180, bc_g1: 0.189, bc_g7: null, base_mv: 2100,
    bullet_type: 'SP', use_cases: ['hunting'],
    construction: 'Notched Jacket Soft Point',
    wound_channel: 'Mushroom Expansion', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 72,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 200,
    manufacturer: 'Winchester', msrp_per_rd: 0.80,
    notes: 'Best whitetail .350 Legend load. Compliant in straight-wall states. Designed for AR-15.',
  },
  {
    id: 'federal_180_fusion_350',
    name: 'Federal Fusion 180gr',
    calibers: ['.350 Legend'],
    grains: 180, bc_g1: 0.198, bc_g7: null, base_mv: 2100,
    bullet_type: 'Fusion', use_cases: ['hunting'],
    construction: 'Fusion Bonded Soft Point',
    wound_channel: 'Controlled Expansion', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 88,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 200,
    manufacturer: 'Federal', msrp_per_rd: 1.00,
    notes: 'Premium .350 Legend deer load. Straight-wall compliant. Better retained weight than PP.',
  },
  {
    id: 'hornady_170_ftx_350',
    name: 'Hornady Custom 170gr FTX',
    calibers: ['.350 Legend'],
    grains: 170, bc_g1: 0.195, bc_g7: null, base_mv: 2200,
    bullet_type: 'FTX', use_cases: ['hunting'],
    construction: 'Polymer Flex-Tip Expanding',
    wound_channel: 'Single Channel', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 80,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 225,
    manufacturer: 'Hornady', msrp_per_rd: 1.10,
    notes: 'Highest velocity .350 Legend at 2,200fps. FTX tip ensures reliable expansion.',
  },

  // ══════════════════════════════════════════════════════════
  // .450 BUSHMASTER
  // ══════════════════════════════════════════════════════════

  {
    id: 'hornady_250_ftx_450',
    name: 'Hornady Black 250gr FTX',
    calibers: ['.450 Bushmaster'],
    grains: 250, bc_g1: 0.210, bc_g7: null, base_mv: 2200,
    bullet_type: 'FTX', use_cases: ['hunting'],
    construction: 'Polymer Flex-Tip Expanding',
    wound_channel: 'Single Channel', penetration_rating: 4, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1500, weight_retention_pct: 80,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 250,
    manufacturer: 'Hornady', msrp_per_rd: 1.60,
    best_use: 'Best .450 Bushmaster all-around load',
    notes: 'Straight-wall compliant. Hard-hitting large game load for AR-15 platform.',
  },
  {
    id: 'federal_260_fusion_450',
    name: 'Federal Fusion 260gr',
    calibers: ['.450 Bushmaster'],
    grains: 260, bc_g1: 0.220, bc_g7: null, base_mv: 2080,
    bullet_type: 'Fusion', use_cases: ['hunting'],
    construction: 'Fusion Bonded Soft Point',
    wound_channel: 'Controlled Expansion', penetration_rating: 4, barrier_rating: 3,
    expansion_reliable: true, min_expansion_vel: 1500, weight_retention_pct: 88,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 225,
    manufacturer: 'Federal', msrp_per_rd: 1.80,
    notes: 'Heavy bonded .450 BM. Excellent for large deer, hogs, and black bear.',
  },
  {
    id: 'winchester_260_pp_450',
    name: 'Winchester Power Point 260gr',
    calibers: ['.450 Bushmaster'],
    grains: 260, bc_g1: 0.218, bc_g7: null, base_mv: 2100,
    bullet_type: 'SP', use_cases: ['hunting'],
    construction: 'Notched Jacket Soft Point',
    wound_channel: 'Mushroom Expansion', penetration_rating: 3, barrier_rating: 2,
    expansion_reliable: true, min_expansion_vel: 1600, weight_retention_pct: 70,
    fbi_protocol: false, le_adopted: false, ethical_hunting: true, ethical_range_yds: 200,
    manufacturer: 'Winchester', msrp_per_rd: 1.40,
    notes: 'Budget .450 BM. Straight-wall compliant. Adequate whitetail performance.',
  },
]
